import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2, Upload, Check, X, Plus, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONS = [
  { value: "formel", label: "Formel" },
  { value: "decontracte", label: "Décontracté" },
  { value: "engageant", label: "Engageant" },
  { value: "humoristique", label: "Humoristique" },
];

const STYLES = [
  { value: "anime_realisme", label: "Anime réaliste", desc: "Style dessin animé moderne, expressif et lumineux." },
  { value: "roman_graphique", label: "Roman graphique", desc: "Trait soigné, ambiance cinématographique et nuancée." },
  { value: "traits_fins", label: "Traits fins", desc: "Ligne claire et épurée, élégante et intemporelle." },
];

const MAX_PER_CAT = 5;

export default function FormulaireClient() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("full");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    activite: "", public_cible: "", ton: [], impression: "",
    style_bd: "", liens_web: [""],
  });
  const [photos, setPhotos] = useState({ visage: [], corps: [] });
  const [uploading, setUploading] = useState({ visage: false, corps: false });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/formulaire/${token}`);
        const cfg = res.data;
        setMode(cfg.mode || "full");
        setForm((f) => ({ ...f, prenom: cfg.prenom || "", nom: cfg.nom || "", email: cfg.email || "" }));
        if (cfg.already_submitted) setDone(true);
      } catch (err) {
        const code = err.response?.status;
        setError(code === 410 ? "Ce lien a expiré. Contacte ton interlocuteur pour en recevoir un nouveau."
          : "Ce lien n'est pas valide. Vérifie l'adresse ou contacte ton interlocuteur.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const steps = mode === "light"
    ? ["identite", "photos", "style", "liens"]
    : ["identite", "contexte", "photos", "style", "liens"];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTon = (v) =>
    setForm((f) => ({ ...f, ton: f.ton.includes(v) ? f.ton.filter((t) => t !== v) : [...f.ton, v] }));

  const handlePhotoSelect = async (cat, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_PER_CAT - photos[cat].length;
    if (room <= 0) { toast.error(`Maximum ${MAX_PER_CAT} photos.`); return; }
    setUploading((u) => ({ ...u, [cat]: true }));
    for (const file of files.slice(0, room)) {
      try {
        const fd = new FormData();
        fd.append("categorie", cat);
        fd.append("file", file);
        const res = await axios.post(`${API}/formulaire/${token}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((p) => ({
          ...p,
          [cat]: [...p[cat], { ref: res.data.ref, filename: res.data.filename, preview: URL.createObjectURL(file) }],
        }));
      } catch (err) {
        toast.error(err.response?.data?.detail || "Échec de l'envoi d'une photo.");
      }
    }
    setUploading((u) => ({ ...u, [cat]: false }));
  };

  const removePhoto = (cat, idx) =>
    setPhotos((p) => {
      const partante = p[cat][idx];
      if (partante?.preview) { URL.revokeObjectURL(partante.preview); }
      return { ...p, [cat]: p[cat].filter((_, i) => i !== idx) };
    });

  const stepValid = () => {
    const s = steps[stepIdx];
    if (s === "identite") return form.prenom && form.nom && form.email && form.telephone;
    if (s === "contexte") return form.activite && form.public_cible && form.ton.length > 0 && form.impression;
    if (s === "photos") return photos.visage.length >= 1 && photos.corps.length >= 1;
    if (s === "style") return !!form.style_bd;
    return true; // liens (optionnel)
  };

  const next = () => { if (stepValid()) setStepIdx((i) => Math.min(i + 1, steps.length - 1)); };
  const prev = () => setStepIdx((i) => Math.max(i - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/formulaire/${token}`, {
        prenom: form.prenom, nom: form.nom, email: form.email, telephone: form.telephone,
        activite: form.activite || null, public_cible: form.public_cible || null,
        ton: form.ton, impression: form.impression || null,
        style_bd: form.style_bd,
        liens_web: form.liens_web.map((l) => l.trim()).filter(Boolean),
        // Les photos réellement gardées : celles retirées de l'écran ne doivent
        // ni servir à la BD ni rester sur le serveur.
        photos: [...photos.visage, ...photos.corps].map((p) => p.filename),
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Réessaie dans un instant.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- écrans d'état ----------
  if (loading) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;
  if (error) return <Centered><Card className="max-w-md"><CardContent className="p-8 text-center space-y-2">
    <X className="h-8 w-8 text-red-500 mx-auto" /><p className="text-base">{error}</p></CardContent></Card></Centered>;
  if (done) return <Centered><Card className="max-w-md"><CardContent className="p-8 text-center space-y-3">
    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center mx-auto"><Check className="h-6 w-6 text-green-600" /></div>
    <h2 className="text-xl font-semibold">Merci, c'est enregistré ! 🎉</h2>
    <p className="text-muted-foreground">Ta demande est bien prise en compte. On revient vers toi rapidement par WhatsApp.</p>
  </CardContent></Card></Centered>;

  const s = steps[stepIdx];
  const pct = Math.round(((stepIdx + 1) / steps.length) * 100);

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" richColors />
      <div className="max-w-lg mx-auto px-4 py-6 sm:py-10">
        <div className="mb-6">
          <h1 className="text-xl font-bold">Ton autobiographie professionnelle</h1>
          <p className="text-sm text-muted-foreground">Quelques infos pour créer ton mini-site et ta BD.</p>
          <div className="mt-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            Ton récit comptera <strong>cinq histoires</strong> — cinq chapitres, et une
            planche de BD par chapitre. On les choisira ensemble pendant l'interview.
          </div>
          <div className="mt-4"><Progress value={pct} /></div>
          <p className="text-xs text-muted-foreground mt-1">Étape {stepIdx + 1} / {steps.length}</p>
        </div>

        <Card><CardContent className="p-5 space-y-5">
          {s === "identite" && (
            <Section title="Tes coordonnées">
              <Field label="Prénom" req><Input value={form.prenom} onChange={(e) => set("prenom", e.target.value)} /></Field>
              <Field label="Nom" req><Input value={form.nom} onChange={(e) => set("nom", e.target.value)} /></Field>
              <Field label="Email" req><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Téléphone" req><Input type="tel" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} /></Field>
            </Section>
          )}

          {s === "contexte" && (
            <Section title="Ton activité & le ton souhaité">
              <Field label="Ton activité professionnelle (en une phrase)" req>
                <Input value={form.activite} onChange={(e) => set("activite", e.target.value)} placeholder="Ex : naturopathe spécialisée en post-partum" /></Field>
              <Field label="À qui s'adresse ton autobiographie ?" req>
                <Input value={form.public_cible} onChange={(e) => set("public_cible", e.target.value)} placeholder="Ex : jeunes parents, futurs clients…" /></Field>
              <Field label="Le ton souhaité (plusieurs choix possibles)" req>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {TONS.map((t) => (
                    <label key={t.value} className="flex items-center gap-2 rounded-md border p-2 cursor-pointer hover:bg-accent">
                      <Checkbox checked={form.ton.includes(t.value)} onCheckedChange={() => toggleTon(t.value)} />
                      <span className="text-sm">{t.label}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Quelle impression veux-tu laisser ?" req>
                <Textarea rows={3} value={form.impression} onChange={(e) => set("impression", e.target.value)} placeholder="Ex : sérieux et bienveillance, expertise accessible…" /></Field>
            </Section>
          )}

          {s === "photos" && (
            <Section title="Tes photos">
              <p className="text-sm text-muted-foreground -mt-1">
                Pour une BD plus ressemblante, ajoute <strong>3-4 portraits nets</strong> (de face, bien éclairés) + 1-2 photos en pied. JPG/PNG, 12 Mo max.
              </p>
              <PhotoUploader label="Photos de visage" hint="min. 1 — idéal 3-4" cat="visage"
                items={photos.visage} uploading={uploading.visage}
                onSelect={(fl) => handlePhotoSelect("visage", fl)} onRemove={(i) => removePhoto("visage", i)} />
              <PhotoUploader label="Photos en pied (corps entier)" hint="min. 1" cat="corps"
                items={photos.corps} uploading={uploading.corps}
                onSelect={(fl) => handlePhotoSelect("corps", fl)} onRemove={(i) => removePhoto("corps", i)} />
            </Section>
          )}

          {s === "style" && (
            <Section title="Le style de ta BD">
              <div className="space-y-2">
                {STYLES.map((st) => (
                  <button key={st.value} type="button" onClick={() => set("style_bd", st.value)}
                    className={`w-full text-left rounded-lg border overflow-hidden transition ${form.style_bd === st.value ? "border-primary ring-2 ring-primary/30" : "hover:bg-accent"}`}>
                    {/* Aperçu portrait affiché ENTIER (object-contain + centré) :
                        object-cover h-28 recadrait l'image et n'en montrait que le
                        haut sur mobile — retours Bruno + Virginie. */}
                    <div className="bg-muted flex justify-center">
                      <img
                        src={`${API}/formulaire/styles/${st.value}/preview`}
                        alt={st.label}
                        loading="lazy"
                        className="h-44 w-auto object-contain"
                      />
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{st.label}</span>
                        {form.style_bd === st.value && <Check className="h-4 w-4 text-primary" />}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{st.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </Section>
          )}

          {s === "liens" && (
            <Section title="Tes liens web (optionnel)">
              <p className="text-sm text-muted-foreground -mt-1">Site, réseaux… ils serviront de bouton d'action en bas de ton mini-site.</p>
              {form.liens_web.map((lien, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={lien} placeholder="Colle ton lien (site, LinkedIn…)"
                    onChange={(e) => set("liens_web", form.liens_web.map((l, j) => (j === i ? e.target.value : l)))} />
                  {form.liens_web.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => set("liens_web", form.liens_web.filter((_, j) => j !== i))}>
                      <X className="h-4 w-4" /></Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => set("liens_web", [...form.liens_web, ""])}>
                <Plus className="h-4 w-4 mr-1" /> Ajouter un lien</Button>
            </Section>
          )}

          {/* navigation */}
          <div className="flex items-center justify-between pt-2">
            <Button variant="ghost" onClick={prev} disabled={stepIdx === 0}>Retour</Button>
            {stepIdx < steps.length - 1 ? (
              <Button onClick={next} disabled={!stepValid()}>Continuer</Button>
            ) : (
              <Button onClick={submit} disabled={!stepValid() || submitting}>
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi…</> : "Envoyer"}
              </Button>
            )}
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-background flex items-center justify-center px-4">{children}</div>;
}

function Section({ title, children }) {
  return <div className="space-y-4"><h2 className="text-base font-semibold">{title}</h2>{children}</div>;
}

function Field({ label, req, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}{req && <span className="text-red-500 ml-0.5">*</span>}</Label>
      {children}
    </div>
  );
}

function PhotoUploader({ label, hint, cat, items, uploading, onSelect, onRemove }) {
  const inputId = `photo-${cat}`;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <Label className="text-sm">{label}</Label>
        <span className="text-xs text-muted-foreground">{hint} · {items.length}/{MAX_PER_CAT}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it, i) => (
          <div key={i} className="relative h-20 w-20 rounded-md overflow-hidden border">
            <img src={it.preview} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => onRemove(i)}
              className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5"><X className="h-3 w-3 text-white" /></button>
          </div>
        ))}
        {items.length < MAX_PER_CAT && (
          <label htmlFor={inputId}
            className="h-20 w-20 rounded-md border-2 border-dashed flex items-center justify-center cursor-pointer hover:bg-accent">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : <Camera className="h-5 w-5 text-muted-foreground" />}
            <input id={inputId} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => { onSelect(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>
    </div>
  );
}
